import { c } from 'ttag';

import { CatalogueEmptyReason } from '../../../graphql/__generated__/schema';
import { refusalCode } from '../../../graphql/refusalCode';

/**
 * Why a catalogue of the onboarding journey has nothing to offer.
 *
 * **Attention, the empty case stays the ordinary case, even though it is no longer the only
 * one.** Until PCG-79 neither `dashboard` nor `partner_list` had any writer at all:
 * no mutation, no ingestion, no screen, so both catalogues answered
 * `EMPTY_CATALOGUE` everywhere. The six mutations and the two back-office screens now
 * exist, so a populated catalogue is reachable; but an account nobody has declared
 * anything on still answers empty, and that is correct rather than a failure. What
 * changed is the **guidance**: there is now somewhere to send the reader.
 *
 * Which is precisely why the wording matters more here than on a list that is
 * usually full: an empty picker rendered as a blank sliver reads as a broken
 * control, and the operator files a bug against a screen that is working.
 *
 * **Three empties, from two channels.** The API says why through `emptyReason`,
 * an enumeration with two values, and refuses through a GraphQL error code when
 * the question itself was not allowed. A missing permission is therefore *not* an
 * `emptyReason` (there is no list to be empty) and the screen has to read both
 * to tell the three apart:
 *
 * - `EMPTY_CATALOGUE`: nothing to offer, and nobody is at fault;
 * - `NO_ACCOUNT_IN_PERIMETER`: the portfolio is empty, so the question has no
 *   ground to stand on;
 * - `MISSING_PERMISSION` (an error): the answer was refused, not empty.
 *
 * Saying « aucun tableau de bord » to somebody who simply may not look would send
 * them declaring a dashboard that already exists.
 */
export interface CatalogueEmptiness {
    /** What is true, stated as a fact about the data: never « une erreur est survenue ». */
    title: string;
    /**
     * What to do about it, or that there is nothing to do. Never « réessayez »
     * unless retrying is genuinely the fix: an empty catalogue answers a click
     * with the same emptiness, forever.
     */
    guidance: string;
    /**
     * How loudly to say it. An empty catalogue is `info`: it is the normal state
     * of the day, and a red box would send the operator hunting for an incident.
     * A refusal is `warning`: the journey survives it, since both steps are
     * optional, but something did go wrong for this operator.
     */
    tone: 'info' | 'warning' | 'danger';
    /** The server's own sentence, kept only when the refusal is one we cannot read. */
    detail?: string;
}

/**
 * The wording shared by the two catalogues. It is shared because the *reason* is
 * the same fact of the platform; what is **not** shared is the sentence naming the
 * thing that is missing, which each step passes in **whole**.
 *
 * Whole, and not as a `${subject}` slotted into one template: French agrees the
 * participle with its subject: « les tableaux de bord n'ont pas pu être chargé**s** »
 * against « les listes de partenaires n'ont pas pu être chargé**es** », so a single
 * interpolated msgid could only ever be right for one of the two.
 */
const refusalEmptiness = (error: unknown, unreadableTitle: string): CatalogueEmptiness => {
    switch (refusalCode(error)) {
        case 'MISSING_PERMISSION':
            return {
                title: c('Onboarding catalogue').t`Your role does not let you manage users.`,
                guidance: c('Onboarding catalogue')
                    .t`So this list was refused rather than answered: it is not empty, it was not read. Ask an administrator for the user-management permission. The rest of the journey will refuse the same way when the invitation is sent.`,
                tone: 'warning',
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Onboarding catalogue').t`One of the selected accounts is outside your portfolio.`,
                guidance: c('Onboarding catalogue')
                    .t`The accounts were refused as a block rather than quietly narrowed, so nothing is offered here. Go back to the account step and drop the account you cannot act on.`,
                tone: 'danger',
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Onboarding catalogue').t`Your session no longer carries an active membership.`,
                guidance: c('Onboarding catalogue')
                    .t`Sign in again. The draft entered so far lives in this page and will not survive it, so copy the email address before you do.`,
                tone: 'danger',
            };
        default:
            return {
                title: unreadableTitle,
                guidance: c('Onboarding catalogue')
                    .t`Nothing is offered below for want of an answer, not for want of entries. Both steps are optional: you can retry, or continue without attributing anything.`,
                tone: 'danger',
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};

/**
 * Why the dashboard catalogue has nothing to offer, or null when it has.
 *
 * `error` wins over `emptyReason`: a refused query carries no reason, and its
 * absent list would otherwise be read as an empty catalogue: the one confusion
 * this whole file exists to prevent.
 */
export const dashboardEmptiness = (
    error: unknown,
    emptyReason: CatalogueEmptyReason | null | undefined
): CatalogueEmptiness | null => {
    if (error) {
        return refusalEmptiness(error, c('Onboarding catalogue').t`The dashboards could not be loaded.`);
    }
    switch (emptyReason) {
        case CatalogueEmptyReason.EmptyCatalogue:
            return {
                title: c('Onboarding catalogue').t`No dashboard is declared on the selected accounts.`,
                guidance: c('Onboarding catalogue')
                    .t`The catalogue is a whitelist entered by hand, never a mirror of Metabase: an account carries nothing until somebody declares it. Nothing is broken and there is nothing to retry. This step is optional — continue, or declare an entry from the users list filtered on the account, then come back.`,
                tone: 'info',
            };
        case CatalogueEmptyReason.NoAccountInPerimeter:
            return {
                title: c('Onboarding catalogue').t`You carry no account, so no dashboard can hang from one.`,
                guidance: c('Onboarding catalogue')
                    .t`A dashboard is the object of an account, and your portfolio holds none. Asking for a portfolio is the way out — declaring a dashboard would change nothing here.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/** Same reading for the partner lists, whose catalogue hangs from no account at all. */
export const partnerListEmptiness = (
    error: unknown,
    emptyReason: CatalogueEmptyReason | null | undefined
): CatalogueEmptiness | null => {
    if (error) {
        return refusalEmptiness(error, c('Onboarding catalogue').t`The partner lists could not be loaded.`);
    }
    switch (emptyReason) {
        case CatalogueEmptyReason.EmptyCatalogue:
            return {
                title: c('Onboarding catalogue').t`No partner list exists yet.`,
                guidance: c('Onboarding catalogue')
                    .t`Lists are a platform referential curated in the back-office, and none has been declared so far. Nothing is broken and there is nothing to retry. This step is optional — continue, or declare one from the « Listes de partenaires » screen, then come back.`,
                tone: 'info',
            };
        case CatalogueEmptyReason.NoAccountInPerimeter:
            // Unreachable by design: this catalogue is a platform referential with no
            // account axis, so no perimeter is applied to it. Read rather than assumed
            // away: a reason we render as nothing would be a blank block if the API
            // ever grew one, which is the defect this file exists to avoid.
            return {
                title: c('Onboarding catalogue').t`The partner lists came back bounded by a perimeter.`,
                guidance: c('Onboarding catalogue')
                    .t`This catalogue is global and has no account axis, so this answer is not one the screen expects. Report it: the journey continues without a list.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};
